/**
 * Встроенный график «Performance over time» в Ads Manager.
 *
 * Тонкая обёртка над `ChartCard` — тем же компонентом, что рисует график
 * Overview. Своего рисования здесь нет: два графика с одинаковым видом и
 * разным кодом однажды разойдутся, и объяснить, почему линия на соседних
 * экранах выглядит по-разному, будет нечем.
 *
 * ★ График строится ТОЛЬКО по одной сущности. При нуле и при двух — то
 * же окно с placeholder'ом, а не закрытие: закрывшись само, оно бы
 * пропало ровно в тот момент, когда человек перекладывает выделение.
 */
import { useEffect, useMemo, useState } from "react";

import { Btn } from "@/components/Btn";
import { I } from "@/components/Icon";
import { ChartCard } from "@/components/overview/ChartCard";
import {
  useChartPresets,
  useDeleteChartPreset,
  useEntityChanges,
  useEntityChart,
  useSaveChartPreset,
  useSetChartLastMetrics,
} from "@/lib/api-hooks";
import { PeriodPicker } from "@/components/PeriodPicker";
import {
  DEFAULT_CHART_METRICS,
  type ChartPeriod,
  aggregatePoints,
  availablePeriods,
  buildEntityMetrics,
  makeMarkerPosition,
  monthStart,
  toChartSeries,
  weekStart,
  withSelectedPlaceholders,
} from "@/lib/entity-chart-metrics";

type Level = "campaign" | "ad_group" | "keyword";

const BADGE: Record<Level, string> = {
  campaign: "Campaign",
  ad_group: "Ad Group",
  keyword: "Keyword",
};

const EMPTY: Record<Level, string> = {
  campaign: "Select one campaign",
  ad_group: "Select one ad group",
  keyword: "Select one keyword",
};

export function EntityChartPanel({
  level,
  appId,
  entityId,
  entityName,
  dateFrom,
  dateTo,
  onClose,
  campaignId,
}: {
  level: Level;
  appId: number | null;
  /** `null` — выделено 0 или ≥2 сущностей: рисуем placeholder. */
  entityId: number | null;
  /** Кампания сущности — вход в индекс для запроса ряда. На уровне
   *  кампании не нужна: там сущность и есть кампания. */
  campaignId?: number | null;
  entityName: string | null;
  dateFrom: string;
  dateTo: string;
  onClose: () => void;
}) {
  const chart = useEntityChart(
    appId, level, entityId, dateFrom, dateTo, campaignId,
  );
  const changes = useEntityChanges(appId, level, entityId, dateFrom, dateTo);
  const presets = useChartPresets(level);
  const remember = useSetChartLastMetrics(level);
  const savePreset = useSaveChartPreset(level);
  const deletePreset = useDeleteChartPreset(level);

  const [metrics, setMetrics] = useState<string[]>(DEFAULT_CHART_METRICS);
  const [period, setPeriod] = useState<ChartPeriod>("daily");
  const [seeded, setSeeded] = useState(false);

  // ★ Последний набор применяется ОДИН раз за монтирование. Применяй его
  // на каждом ответе — и выбор, сделанный только что, откатывался бы к
  // сохранённому на первом же перезапросе.
  useEffect(() => {
    if (seeded || presets.isLoading) return;
    const last = presets.data?.last_metrics;
    if (last && last.length > 0) setMetrics(last);
    setSeeded(true);
  }, [seeded, presets.isLoading, presets.data]);

  const periods = useMemo(
    () => availablePeriods(dateFrom, dateTo),
    [dateFrom, dateTo],
  );

  // ★ Сузился отрезок — недоступный режим обязан отпустить. Оставь его,
  // и график молча покажет одну точку на периоде, где недели уже нет.
  useEffect(() => {
    if (!periods.includes(period)) setPeriod("daily");
  }, [periods, period]);

  // ★ Через useMemo, а не выражением: `?? []` создаёт НОВЫЙ массив на
  // каждый рендер, и всё, что зависит от него, пересчитывалось бы даже
  // когда данные не менялись — включая агрегацию всего ряда.
  const rawPoints = useMemo(() => chart.data?.points ?? [], [chart.data]);
  const points = useMemo(
    () => aggregatePoints(rawPoints, period),
    [rawPoints, period],
  );

  // ★ Плюс заглушка на каждую ВЫБРАННУЮ метрику, которой в данных
  // сущности нет: она остаётся в меню (снять её иначе нечем) и в легенде
  // под своим именем с «—». Сам выбор от данных не зависит.
  const extraMetrics = useMemo(
    () =>
      withSelectedPlaceholders(
        buildEntityMetrics(points, chart.data?.events ?? []),
        metrics,
      ),
    [points, chart.data, metrics],
  );

  // ★ Подписи оси. В дневном режиме — прежнее поведение (через одну).
  // В недельном отмечаются понедельники, в месячном — первые числа: сами
  // точки при этом стоят на началах групп, и подпись совпадает с точкой.
  const xEmphasis = useMemo(() => {
    if (period === "daily") return undefined;
    const mark = period === "weekly" ? weekStart : monthStart;
    return points.map((p) => mark(p.date) === p.date);
  }, [points, period]);

  // Значения `ChartCard` берёт из `extraMetrics` по замыканию (§36), а
  // отсюда — даты И охват группы. Проекция вынесена в `toChartSeries`:
  // именно здесь охват раньше терялся, и подписи врали.
  const series = useMemo(() => toChartSeries(points), [points]);

  // ★ Высота холста считается от ОКНА, а не задаётся числом.
  //
  // По дизайну график — верхняя треть, таблица — две трети и с рабочей
  // прокруткой. Фиксированные 260 px дизайна сняты с макета на широком
  // экране: на ноутбуке 900 px они съедают у таблицы столько, что
  // прокручивать становится нечего — ровно то, на что пожаловался
  // пользователь. Доля от окна держит пропорцию на любой высоте, а
  // потолок 260 не даёт графику раздуться на большом мониторе.
  //
  // 96 px — шапка карточки с легендой, поля и полоса маркеров: то, что
  // занимает панель СВЕРХ холста. Считается из доступной колонки
  // (окно минус шапка 60 + крошки 38 + панель действий 74 = 172).
  const chartHeight = useChartHeight();

  const markerPosition = useMemo(
    () => makeMarkerPosition(rawPoints, points, period),
    [points, rawPoints, period],
  );

  const markers = useMemo(
    () =>
      (changes.data?.days ?? []).map((d) => ({
        day: d.day,
        changes: d.changes.map((c) => ({
          ts_utc: c.ts_utc, what: c.what,
          actor_kind: c.actor_kind, actor_label: c.actor_label,
        })),
      })),
    [changes.data],
  );

  const onMetricsChange = (ids: string[]) => {
    setMetrics(ids);
    // Промах здесь ничего не ломает: не запомнили — в следующий раз
    // откроется дефолт уровня. Ронять из-за этого график незачем.
    remember.mutate(ids);
  };

  const single = entityId != null;

  return (
    <ChartCard
      series={single ? series : []}
      metrics={metrics}
      onMetricsChange={onMetricsChange}
      extraMetrics={extraMetrics}
      loading={single && chart.isLoading}
      // ★ `isFetching`, а не `isLoading`: второй ЛОЖЕН на перерисовке —
      // данные уже есть, идёт лишь новый запрос. Именно на этом пути
      // экран и выглядел зависшим.
      //
      // ★★ И ТОЛЬКО график, без `changes`. Маркеры — отдельный запрос, и
      // он медленнее: замер на проде 31.08 дал медиану 6.4 с у
      // `/api/stats/entity-changes` против 4.5 с у `/api/stats/entity-chart`.
      // Пока он шёл, весь график гасился до 45% и накрывался надписью
      // «Preparing data…», хотя линии были уже построены. Человек видел
      // «ещё считается» всё время работы БОЛЕЕ МЕДЛЕННОГО из двух запросов,
      // а не своего. Маркеры ничего не закрывают и просто появятся под
      // осью, когда приедут.
      refetching={single && chart.isFetching}
      periodPicker={
        <PeriodPicker value={period} options={periods} onChange={setPeriod} />
      }
      xEmphasis={xEmphasis}
      fitWidth
      chartHeight={chartHeight}
      // ★ Маркеры стоят на СВОИХ днях во всех режимах. Владелец назвал
      // это «супер важно»: на недельном графике должно быть видно, в
      // какой именно день что менялось, а не «где-то на этой неделе».
      markerPosition={markerPosition}
      // ★ Дропдаун ограничен метриками ЭТОГО графика. Реестр Overview
      // предлагал бы здесь Trials, ROAS и pProfit — величины, которых в
      // ответе эндпоинта нет: выбрал бы такую, и линия легла бы на ноль.
      onlyMetrics={extraMetrics}
      presets={{
        items: presets.data?.presets ?? [],
        onApply: onMetricsChange,
        onDelete: (id) => deletePreset.mutate(id),
        onSave: (name) => savePreset.mutate({ name, metrics }),
        saving: savePreset.isPending,
      }}
      emptyText={single ? undefined : EMPTY[level]}
      changeMarkers={markers}
      title={
        single ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            <span className="chart-entity-name" title={entityName ?? undefined}>
              {entityName ?? `#${entityId}`}
            </span>
            <span className="chart-level-badge">{BADGE[level]}</span>
          </div>
        ) : (
          <h3 style={{ color: "var(--c-text-3)" }}>Performance over time</h3>
        )
      }
      headerExtra={
        <Btn icon={I.x} onClick={onClose} title="Close" aria-label="Close chart" />
      }
    />
  );
}


/** Треть доступной колонки под холст графика, 150…260 px. */
function useChartHeight(): number {
  const calc = () =>
    Math.round(
      Math.max(150, Math.min(260, (window.innerHeight - 172) / 3 - 96)),
    );
  const [h, setH] = useState(calc);
  useEffect(() => {
    const on = () => setH(calc());
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return h;
}
